import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, Eye } from 'lucide-react';
import api from '@/lib/API';

export const NormalSearch = ({ selectedItems = [], onSelectMulti, onFocus, onBlur }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredPreview, setHoveredPreview] = useState(null);
  
  const [purchaseMasterData, setPurchaseMasterData] = useState([]);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchPurchaseMaster = async () => {
      try {
        const res = await api.get('/v1/purchase-master');
        if (res.data) {
          setPurchaseMasterData(res.data.data || res.data || []);
        }
      } catch (e) {
        console.error('Failed to fetch purchase master data', e);
      }
    };
    fetchPurchaseMaster();
  }, []);

  const resolveVendor = (i) => {
    if (!i) return 'Unknown';
    if (i.vendorName) return i.vendorName;
    if (typeof i.vendor === 'object' && i.vendor) return i.vendor.vendorName || i.vendor.name || i.vendor.title || 'Unknown';
    if (i.vendor) return i.vendor;
    if (typeof i.supplier === 'object' && i.supplier) return i.supplier.name || i.supplier.title || 'Unknown';
    if (i.supplier) return i.supplier;
    return 'blum';
  };

  const uniqueProducts = useMemo(() => {
    const uniqueMap = new Map();
    (purchaseMasterData || []).forEach(item => {
      if (item.status === 'Inactive') return;
      const name = String(item.information || item.description || item.itemName || item.name || item.articleId || item._id || '').trim();
      if (name && !uniqueMap.has(name)) {
        uniqueMap.set(name, item);
      }
    });
    return Array.from(uniqueMap.values());
  }, [purchaseMasterData]);

  const availableItems = useMemo(() => {
    let items = uniqueProducts;
    if (!searchTerm) return items.slice(0, 50);
    
    const term = searchTerm.toLowerCase();
    return items.filter(ai => {
      return (
        (ai.information || '').toLowerCase().includes(term) ||
        (ai.description || '').toLowerCase().includes(term) ||
        (ai.itemName || ai.name || '').toLowerCase().includes(term) ||
        (ai.articleId || ai.itemCode || '').toLowerCase().includes(term) ||
        (ai.category || ai.categoryName || '').toLowerCase().includes(term) ||
        (ai.subCategory || '').toLowerCase().includes(term)
      );
    });
  }, [uniqueProducts, searchTerm]);

  const handleToggleSelect = (selItem, checked) => {
    const displayName = selItem.articleId 
      ? `${selItem.articleId} - ${selItem.materialDescription || selItem.description}` 
      : (selItem.itemName || selItem.description || selItem.name || 'Item');
    const rate = selItem.vervePrice || selItem.blumPrice || selItem.price || selItem.mrp || selItem.purchaseRate || selItem.rate || 0;
    
    onSelectMulti({ name: displayName, amount: rate, id: selItem._id || selItem.id || selItem.inventoryId }, checked, selItem);
  };

  const fmt = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0);

  return (
    <div className="relative flex-1" ref={searchContainerRef}>
      <input
        type="text"
        placeholder="Search Inventory (Name, Article ID, Description)..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => {
          setShowDropdown(true);
          if (onFocus) onFocus();
        }}
        onBlur={() => {
          if (onBlur) onBlur();
        }}
        className="w-full h-8 text-[12px] text-slate-700 bg-white border border-slate-200 rounded px-2 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-colors shadow-sm placeholder:text-slate-300"
      />
      {showDropdown && (
        <div className="absolute z-[9999] top-full left-0 mt-1 w-[600px] max-w-[100%] bg-white rounded-md shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] border border-slate-200 h-[220px] flex overflow-hidden">
          {/* LEFT PANE: Search Results */}
          <div className="w-[55%] flex flex-col border-r border-slate-200 bg-white">
            <div className="flex-1 overflow-auto custom-scrollbar">
              {availableItems.length > 0 ? (
                availableItems.map((ai, idx) => {
                  const displayName = ai.articleId 
                    ? `${ai.articleId} - ${ai.materialDescription || ai.description}` 
                    : (ai.itemName || ai.description || ai.name || 'Item');
                  const isSelected = selectedItems.some(i => 
                    i._id === ai._id || 
                    i.name === displayName ||
                    (i.rawData?.articleId && ai.articleId && String(i.rawData.articleId).trim() === String(ai.articleId).trim())
                  ); 
                  
                  return (
                    <div
                      key={idx}
                      onMouseEnter={() => setHoveredPreview(ai)}
                      className="px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 flex justify-between items-center transition-colors cursor-default"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <input 
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleToggleSelect(ai, e.target.checked)}
                          className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-600 cursor-pointer"
                        />
                        <div>
                          <div className="text-[12px] font-bold text-slate-800 line-clamp-1">{ai.information || ai.description || ai.itemName || ai.name}</div>
                          <div className="text-[10px] text-slate-500 mt-0.5 flex gap-2">
                            <span>ID: {ai.articleId || ai.itemCode || ai.code || 'N/A'}</span>
                            {ai.category && <span>• {ai.category}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right">
                          <div className="text-[11px] font-bold text-indigo-600">{Number(ai.availableQty ?? ai.stock ?? ai.quantity ?? 0)} available</div>
                          <div className="text-[10px] text-slate-500 mt-0.5">{resolveVendor(ai)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-8 text-center text-sm text-slate-500">No available items found matching "{searchTerm}"</div>
              )}
            </div>
          </div>

          {/* RIGHT PANE: Toolkit Preview */}
          <div className="w-[45%] bg-slate-50/80 overflow-auto p-4 custom-scrollbar">
            {hoveredPreview ? (
              <div className="animate-in fade-in">
                <h4 className="text-[11px] font-bold text-slate-400 capitalize tracking-widest mb-3 pb-2 border-b border-slate-200">
                  Item Details Preview
                </h4>
                
                <div className="text-[12px] font-bold text-slate-900 mb-4 leading-snug break-words">
                  {hoveredPreview.information || hoveredPreview.description || hoveredPreview.itemName}
                </div>
                
                <div className="grid grid-cols-2 gap-y-3 gap-x-3">
                  <div className="flex flex-col pr-1">
                    <span className="text-[9px] font-semibold text-slate-500 mb-0.5">Article ID</span>
                    <span className="text-[10px] font-semibold text-slate-900 break-all">{hoveredPreview.articleId || hoveredPreview.itemCode || '—'}</span>
                  </div>
                  <div className="flex flex-col pr-1">
                    <span className="text-[9px] font-semibold text-slate-500 mb-0.5">Category</span>
                    <span className="text-[10px] font-semibold text-slate-900 break-words">{hoveredPreview.category || '—'}</span>
                  </div>
                  <div className="flex flex-col pr-1">
                    <span className="text-[9px] font-semibold text-slate-500 mb-0.5">Vendor</span>
                    <span className="text-[10px] font-semibold text-slate-900 break-words">{resolveVendor(hoveredPreview)}</span>
                  </div>
                  <div className="flex flex-col pr-1">
                    <span className="text-[9px] font-semibold text-slate-500 mb-0.5">Available Stock</span>
                    <span className="text-[11px] font-bold text-indigo-600">{Number(hoveredPreview.availableQty ?? hoveredPreview.stock ?? hoveredPreview.quantity ?? 0)}</span>
                  </div>
                  <div className="flex flex-col pr-1">
                    <span className="text-[9px] font-semibold text-slate-500 mb-0.5">Blum Price</span>
                    <span className="text-[10px] font-semibold text-slate-900 break-words">{hoveredPreview.blumPrice ? fmt(hoveredPreview.blumPrice) : '—'}</span>
                  </div>
                  <div className="flex flex-col pr-1">
                    <span className="text-[9px] font-semibold text-slate-500 mb-0.5">Verve Price</span>
                    <span className="text-[10px] font-semibold text-slate-900 break-words">{hoveredPreview.vervePrice ? fmt(hoveredPreview.vervePrice) : '—'}</span>
                  </div>
                  <div className="flex flex-col col-span-2">
                    <span className="text-[9px] font-semibold text-slate-500 mb-0.5">Remarks / Info</span>
                    <span className="text-[10px] font-medium text-slate-700 break-words">{hoveredPreview.remark || hoveredPreview.remarks || '—'}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-400 space-y-3">
                <Eye size={24} className="opacity-20" />
                <div className="text-[11px] font-medium text-center px-4">Hover over an item to preview details</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
